import { type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes, type SelectHTMLAttributes } from 'react';
import { type ApiError } from '../lib/api';

/**
 * Form-level errors object derived from a 400 ValidationProblemDetails response.
 * Key = the offending property path (camelCased to match the server), value = error message.
 * Use <code>fieldErrorsFromApiError(err)</code> to construct.
 */
export type FieldErrors = Record<string, string[]>;

/**
 * Extracts the per-field error map from an ApiError thrown by `api.ts`. The backend's
 * `FluentValidationActionFilter` returns errors in the standard `ValidationProblemDetails`
 * shape: `{ errors: { fieldName: ["message", ...], ... } }`.
 */
export function fieldErrorsFromApiError(err: unknown): FieldErrors {
  if (!err || typeof err !== 'object') return {};
  const body = (err as ApiError).body as { errors?: FieldErrors } | undefined;
  return body?.errors ?? {};
}

interface FormShellProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  /** Top-level error message (e.g. "Email already taken"). Per-field errors are rendered next to the field. */
  formError?: string | null;
  className?: string;
}

/**
 * Layout shell for a form. Wraps children in a vertical-stacked `<form>` with consistent
 * spacing, and renders a top-level error banner when `formError` is set. Pair with
 * <Field>, <TextArea>, <Select> for consistent label/error layout.
 */
export function FormShell({ onSubmit, children, formError, className }: FormShellProps) {
  return (
    <form onSubmit={onSubmit} className={`flex flex-col gap-4 ${className ?? ''}`}>
      {formError ? (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-900 text-sm rounded px-3 py-2">
          {formError}
        </div>
      ) : null}
      {children}
    </form>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  name: string;
  errors?: FieldErrors;
  help?: string;
}

/** Standard label + input + per-field error layout. */
export function Field({ label, name, errors, help, className, ...inputProps }: FieldProps) {
  const fieldErrors = lookupErrors(errors, name);
  return (
    <label className="text-sm flex flex-col gap-1">
      <span className="text-slate-700">{label}</span>
      <input
        name={name}
        className={`border rounded p-2 ${fieldErrors.length ? 'border-red-400' : 'border-slate-300'} ${className ?? ''}`}
        {...inputProps}
      />
      <FieldFeedback errors={fieldErrors} help={help} />
    </label>
  );
}

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  name: string;
  errors?: FieldErrors;
  help?: string;
}

export function TextArea({ label, name, errors, help, className, ...props }: TextAreaProps) {
  const fieldErrors = lookupErrors(errors, name);
  return (
    <label className="text-sm flex flex-col gap-1">
      <span className="text-slate-700">{label}</span>
      <textarea
        name={name}
        className={`border rounded p-2 ${fieldErrors.length ? 'border-red-400' : 'border-slate-300'} ${className ?? ''}`}
        {...props}
      />
      <FieldFeedback errors={fieldErrors} help={help} />
    </label>
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  name: string;
  errors?: FieldErrors;
  help?: string;
  options: { value: string; label: string }[];
}

export function Select({ label, name, errors, help, options, className, ...props }: SelectProps) {
  const fieldErrors = lookupErrors(errors, name);
  return (
    <label className="text-sm flex flex-col gap-1">
      <span className="text-slate-700">{label}</span>
      <select
        name={name}
        className={`border rounded p-2 ${fieldErrors.length ? 'border-red-400' : 'border-slate-300'} ${className ?? ''}`}
        {...props}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <FieldFeedback errors={fieldErrors} help={help} />
    </label>
  );
}

function lookupErrors(errors: FieldErrors | undefined, name: string): string[] {
  if (!errors) return [];
  // ASP.NET ModelState keys arrive PascalCased; tolerate both.
  return errors[name] ?? errors[capitalize(name)] ?? [];
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

function FieldFeedback({ errors, help }: { errors: string[]; help?: string }) {
  if (errors.length) {
    return (
      <span className="text-xs text-red-700">
        {errors.map((e, i) => (<span key={i} className="block">{e}</span>))}
      </span>
    );
  }
  if (help) return <span className="text-xs text-slate-500">{help}</span>;
  return null;
}
